import React, { useCallback, useMemo } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import { Button, Modal } from "@mapmycustomers/ui";

import { findServiceByType } from "@app/util/integrations/useServiceDescription";

import styles from "./WizardModal.module.scss";

interface Props {
  onHide: () => void;
  onSelectService: (isSandbox?: boolean) => void;
  selectedService?: IntegrationService;
  visible: boolean;
}

const WizardModal: React.FC<Props> = ({ onHide, onSelectService, selectedService, visible }) => {
  const intl = useIntl();

  const service = useMemo(() => findServiceByType(selectedService), [selectedService]);

  const handleSelectService = useCallback(() => onSelectService(), [onSelectService]);

  const handleSelectServiceWithSandbox = useCallback(
    () => onSelectService(true),
    [onSelectService]
  );

  if (!service) {
    return null;
  }

  return (
    <Modal
      footer={[
        <div className={styles.footer} key="wizard-footer">
          <div className={styles.sandbox}>
            {service?.sandboxUrl && (
              <>
                <FormattedMessage
                  id="integrations.wizardModal.footer.sandboxText"
                  defaultMessage="Looking to test with an isolated environment? <a>Sync with my {serviceName} sandbox</a>"
                  description="Sandbox Information text"
                  values={{
                    a: (text: string) => (
                      <Button
                        className={styles.link}
                        onClick={handleSelectServiceWithSandbox}
                        type="link"
                      >
                        {text}
                      </Button>
                    ),
                    serviceName: service.name,
                  }}
                />
              </>
            )}
          </div>
          <Button onClick={handleSelectService} type="primary">
            <FormattedMessage
              id="integrations.wizardModal.footer.continue"
              defaultMessage="Let's Go!"
              description="Text of GOT IT button"
            />
          </Button>
        </div>,
      ]}
      onCancel={onHide}
      open={visible}
      title={intl.formatMessage(
        {
          id: "integrations.wizardModal.title",
          defaultMessage: "{serviceName} Integration",
          description: "Title wizard modal",
        },
        { serviceName: service.name }
      )}
    >
      <FormattedMessage
        id="integrations.wizardModal.information"
        defaultMessage="Proceed to {serviceName} to verify your account and give us permission to access your data."
        description="Information text"
        values={{ serviceName: service.name }}
      />
    </Modal>
  );
};

export default WizardModal;
